import React from 'react';
import {
  Tile,
  TextInput,
  Toggle,
  Select,
  SelectItem,
  Button,
  NumberInput,
} from '@carbon/react';
import {
  Notification,
  Temperature,
  ChartLine,
  Location,
  Time,
  DataBase,
} from '@carbon/icons-react';
import {
  RETENTION_DAY_OPTIONS,
  STATUS_TREND_WINDOWS,
  SYNC_INTERVAL_OPTIONS,
  UPDATE_FREQUENCY_OPTIONS,
  ZONE_TREND_OPTIONS,
} from '../../services/appSettingsSchema';

const tempUnitLabel = (unit) => (unit === 'fahrenheit' ? '°F' : '°C');

/**
 * Settings sections aligned with native SettingsScreen (SensorContext AppSettings).
 */
export default function NativeSettingsForm({
  settings,
  onChange,
  onSave,
  isSaving,
  section,
}) {
  if (!settings) return null;

  const set = (patch) => onChange(patch);
  const unit = settings.temperatureUnit;

  const statusTrendLabel = () => {
    const h = settings.statusTrendTimeWindow;
    if (h < 1) return `${Math.round(h * 60)}min`;
    return `${h}h`;
  };

  const zoneTrendHelp = {
    AGGRESSIVE: 'If ANY sensor shows a trend, the zone shows that trend',
    CONSERVATIVE: 'Zone shows the most common trend among sensors',
    BALANCED: 'Priority: RISING > FALLING > STEADY > UNKNOWN',
  };

  if (section === 'display') {
    return (
      <Tile className="settings-page__card">
        <div className="settings-page__card-header">
          <ChartLine size={20} />
          <h3>Display & Units</h3>
        </div>
        <div className="settings-page__card-content">
          <p className="settings-page__description">Matches native app chart and unit preferences.</p>
          <div className="settings-page__field-row">
            <Select
              id="temperatureUnit"
              labelText="Temperature units"
              value={settings.temperatureUnit}
              onChange={(e) => set({ temperatureUnit: e.target.value })}
            >
              <SelectItem value="celsius" text="Celsius (°C)" />
              <SelectItem value="fahrenheit" text="Fahrenheit (°F)" />
            </Select>
            <Select
              id="distanceUnit"
              labelText="Distance units"
              value={settings.distanceUnit}
              onChange={(e) => set({ distanceUnit: e.target.value })}
            >
              <SelectItem value="m" text="Meters (m)" />
              <SelectItem value="ft" text="Feet (ft)" />
            </Select>
          </div>
          <div className="settings-page__field-row">
            <NumberInput
              id="chartTempScale"
              label="Chart temperature increment"
              value={settings.chartScales?.temperature ?? 2}
              onChange={(e, { value }) =>
                set({ chartScales: { ...settings.chartScales, temperature: value } })
              }
              min={1}
              max={10}
              step={1}
            />
            <NumberInput
              id="chartHumidityScale"
              label="Chart humidity increment (%)"
              value={settings.chartScales?.humidity ?? 5}
              onChange={(e, { value }) =>
                set({ chartScales: { ...settings.chartScales, humidity: value } })
              }
              min={2}
              max={20}
              step={2}
            />
          </div>
          <Button kind="primary" onClick={() => onSave('display')} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Display Settings'}
          </Button>
        </div>
      </Tile>
    );
  }

  if (section === 'sms') {
    return (
      <Tile className="settings-page__card">
        <div className="settings-page__card-header">
          <Notification size={20} />
          <h3>SMS Text Message Alerts</h3>
        </div>
        <div className="settings-page__card-content">
          <div className="settings-page__toggle-row">
            <div className="settings-page__toggle-info">
              <span className="settings-page__toggle-label">Enable SMS alerts</span>
              <span className="settings-page__toggle-desc">Native: alertsEnabled</span>
            </div>
            <Toggle
              id="alertsEnabled"
              toggled={settings.alertsEnabled}
              onToggle={() => set({ alertsEnabled: !settings.alertsEnabled })}
              hideLabel
            />
          </div>
          {settings.alertsEnabled && (
            <>
              <TextInput
                id="alertPhone"
                labelText="Phone number (with country code)"
                value={settings.alertPhone}
                onChange={(e) => set({ alertPhone: e.target.value })}
                placeholder="+1234567890"
                helperText="Same as native Settings → SMS alerts phone field"
              />
              <p className="settings-page__description">Temperature alert thresholds (SMS)</p>
              <ThresholdPair
                unit={unit}
                minEnabled={settings.tempAlertMinEnabled}
                maxEnabled={settings.tempAlertMaxEnabled}
                minValue={settings.tempAlertMin}
                maxValue={settings.tempAlertMax}
                onMinEnabled={(v) => set({ tempAlertMinEnabled: v })}
                onMaxEnabled={(v) => set({ tempAlertMaxEnabled: v })}
                onMinValue={(v) => set({ tempAlertMin: v })}
                onMaxValue={(v) => set({ tempAlertMax: v })}
              />
              <p className="settings-page__description">Humidity alert thresholds (SMS)</p>
              <ThresholdPair
                isHumidity
                minEnabled={settings.humidityAlertMinEnabled}
                maxEnabled={settings.humidityAlertMaxEnabled}
                minValue={settings.humidityAlertMin}
                maxValue={settings.humidityAlertMax}
                onMinEnabled={(v) => set({ humidityAlertMinEnabled: v })}
                onMaxEnabled={(v) => set({ humidityAlertMaxEnabled: v })}
                onMinValue={(v) => set({ humidityAlertMin: v })}
                onMaxValue={(v) => set({ humidityAlertMax: v })}
              />
            </>
          )}
          <Button kind="primary" onClick={() => onSave('sms')} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save SMS Settings'}
          </Button>
        </div>
      </Tile>
    );
  }

  if (section === 'push') {
    return (
      <Tile className="settings-page__card">
        <div className="settings-page__card-header">
          <Notification size={20} />
          <h3>Push Notifications</h3>
        </div>
        <div className="settings-page__card-content">
          <div className="settings-page__toggle-row">
            <div className="settings-page__toggle-info">
              <span className="settings-page__toggle-label">Push notifications</span>
              <span className="settings-page__toggle-desc">Native: pushNotificationsEnabled</span>
            </div>
            <Toggle
              id="pushNotificationsEnabled"
              toggled={settings.pushNotificationsEnabled}
              onToggle={() => set({ pushNotificationsEnabled: !settings.pushNotificationsEnabled })}
              hideLabel
            />
          </div>
          {settings.pushNotificationsEnabled && (
            <>
              <p className="settings-page__description">
                Thresholds sync to cloud alert_settings (temperatureMin/Max, humidityMin/Max) like native.
              </p>
              <ThresholdPair
                unit={unit}
                minEnabled={settings.pushTempAlertMinEnabled}
                maxEnabled={settings.pushTempAlertMaxEnabled}
                minValue={settings.pushTempAlertMin}
                maxValue={settings.pushTempAlertMax}
                onMinEnabled={(v) => set({ pushTempAlertMinEnabled: v })}
                onMaxEnabled={(v) => set({ pushTempAlertMaxEnabled: v })}
                onMinValue={(v) => set({ pushTempAlertMin: v })}
                onMaxValue={(v) => set({ pushTempAlertMax: v })}
                labelPrefix="Push temp"
              />
              <ThresholdPair
                isHumidity
                minEnabled={settings.pushHumidityAlertMinEnabled}
                maxEnabled={settings.pushHumidityAlertMaxEnabled}
                minValue={settings.pushHumidityAlertMin}
                maxValue={settings.pushHumidityAlertMax}
                onMinEnabled={(v) => set({ pushHumidityAlertMinEnabled: v })}
                onMaxEnabled={(v) => set({ pushHumidityAlertMaxEnabled: v })}
                onMinValue={(v) => set({ pushHumidityAlertMin: v })}
                onMaxValue={(v) => set({ pushHumidityAlertMax: v })}
                labelPrefix="Push humidity"
              />
            </>
          )}
          <Button kind="primary" onClick={() => onSave('push')} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Push Settings'}
          </Button>
        </div>
      </Tile>
    );
  }

  if (section === 'webAlerts') {
    return (
      <Tile className="settings-page__card">
        <div className="settings-page__card-header">
          <Notification size={20} />
          <h3>Web Email & Status Alerts</h3>
        </div>
        <div className="settings-page__card-content">
          <div className="settings-page__toggle-row">
            <div className="settings-page__toggle-info">
              <span className="settings-page__toggle-label">Email alerts</span>
              <span className="settings-page__toggle-desc">Temperature/status emails via Resend (web)</span>
            </div>
            <Toggle
              id="emailAlerts"
              toggled={settings.emailAlerts}
              onToggle={() => set({ emailAlerts: !settings.emailAlerts })}
              hideLabel
            />
          </div>
          <div className="settings-page__toggle-row">
            <div className="settings-page__toggle-info">
              <span className="settings-page__toggle-label">Status alerts</span>
              <span className="settings-page__toggle-desc">Email/SMS when gateway or sensor goes offline</span>
            </div>
            <Toggle
              id="statusAlerts"
              toggled={settings.statusAlerts}
              onToggle={() => set({ statusAlerts: !settings.statusAlerts })}
              hideLabel
            />
          </div>
          <Button kind="primary" onClick={() => onSave('webAlerts')} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Web Alert Settings'}
          </Button>
        </div>
      </Tile>
    );
  }

  if (section === 'trends') {
    return (
      <>
        <Tile className="settings-page__card">
          <div className="settings-page__card-header">
            <Location size={20} />
            <h3>Status Trend Settings</h3>
          </div>
          <div className="settings-page__card-content">
            <Select
              id="statusTrendTimeWindow"
              labelText="Analysis time window"
              value={statusTrendLabel()}
              onChange={(e) => {
                const opt = STATUS_TREND_WINDOWS.find((w) => w.label === e.target.value);
                if (opt) set({ statusTrendTimeWindow: opt.hours });
              }}
            >
              {STATUS_TREND_WINDOWS.map((w) => (
                <SelectItem key={w.label} value={w.label} text={w.label} />
              ))}
            </Select>
            <Select
              id="zoneTrendCalculation"
              labelText="Zone trend calculation"
              value={settings.zoneTrendCalculation}
              onChange={(e) => set({ zoneTrendCalculation: e.target.value })}
              helperText={zoneTrendHelp[settings.zoneTrendCalculation]}
            >
              {ZONE_TREND_OPTIONS.map((z) => (
                <SelectItem key={z} value={z} text={z.charAt(0) + z.slice(1).toLowerCase()} />
              ))}
            </Select>
            <div className="settings-page__field-row">
              <NumberInput
                id="temperatureThreshold"
                label={`Temperature trend threshold (${tempUnitLabel(unit)}/hr)`}
                value={settings.temperatureThreshold}
                onChange={(e, { value }) => set({ temperatureThreshold: value })}
                min={0.1}
                max={5}
                step={0.1}
              />
              <NumberInput
                id="humidityThreshold"
                label="Humidity trend threshold (%/hr)"
                value={settings.humidityThreshold}
                onChange={(e, { value }) => set({ humidityThreshold: value })}
                min={0.5}
                max={10}
                step={0.5}
              />
            </div>
            <Button kind="primary" onClick={() => onSave('trends')} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Trend Settings'}
            </Button>
          </div>
        </Tile>
      </>
    );
  }

  if (section === 'data') {
    return (
      <>
        <Tile className="settings-page__card">
          <div className="settings-page__card-header">
            <Time size={20} />
            <h3>Data Frequency</h3>
          </div>
          <div className="settings-page__card-content">
            <Select
              id="updateFrequency"
              labelText="Database save frequency (native updateFrequency)"
              value={String(settings.updateFrequency)}
              onChange={(e) => set({ updateFrequency: Number(e.target.value) })}
              helperText="Changing this also sets cloud sync interval (minutes × 60 seconds)."
            >
              {UPDATE_FREQUENCY_OPTIONS.map((m) => (
                <SelectItem key={m} value={String(m)} text={`${m} min`} />
              ))}
            </Select>
            <Select
              id="syncInterval"
              labelText="Cloud sync frequency (seconds)"
              value={String(settings.syncInterval)}
              onChange={(e) => set({ syncInterval: Number(e.target.value) })}
            >
              {SYNC_INTERVAL_OPTIONS.map((s) => (
                <SelectItem key={s} value={String(s)} text={`${s}s (${s / 60} min)`} />
              ))}
            </Select>
            <Button kind="primary" onClick={() => onSave('data')} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Data Settings'}
            </Button>
          </div>
        </Tile>
        <Tile className="settings-page__card">
          <div className="settings-page__card-header">
            <DataBase size={20} />
            <h3>Data Retention Policy</h3>
          </div>
          <div className="settings-page__card-content">
            <div className="settings-page__toggle-row">
              <div className="settings-page__toggle-info">
                <span className="settings-page__toggle-label">Enable auto data retention</span>
                <span className="settings-page__toggle-desc">Native: retentionEnabled</span>
              </div>
              <Toggle
                id="retentionEnabled"
                toggled={settings.retentionEnabled}
                onToggle={() => set({ retentionEnabled: !settings.retentionEnabled })}
                hideLabel
              />
            </div>
            {settings.retentionEnabled && (
              <Select
                id="retentionDays"
                labelText="Retention period"
                value={String(settings.retentionDays)}
                onChange={(e) => set({ retentionDays: Number(e.target.value) })}
              >
                {RETENTION_DAY_OPTIONS.map((d) => (
                  <SelectItem
                    key={d}
                    value={String(d)}
                    text={d === 90 ? '3 months' : d === 180 ? '6 months' : d === 270 ? '9 months' : '12 months'}
                  />
                ))}
              </Select>
            )}
            <Button kind="primary" onClick={() => onSave('retention')} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Retention Settings'}
            </Button>
          </div>
        </Tile>
      </>
    );
  }

  return null;
}

function ThresholdPair({
  unit = 'celsius',
  isHumidity = false,
  labelPrefix = 'Alert',
  minEnabled,
  maxEnabled,
  minValue,
  maxValue,
  onMinEnabled,
  onMaxEnabled,
  onMinValue,
  onMaxValue,
}) {
  const suffix = isHumidity ? '%' : tempUnitLabel(unit);
  return (
    <>
      <div className="settings-page__toggle-row">
        <span className="settings-page__toggle-label">{labelPrefix} — minimum</span>
        <Toggle id={`${labelPrefix}-min-en`} toggled={minEnabled} onToggle={() => onMinEnabled(!minEnabled)} hideLabel />
      </div>
      {minEnabled && (
        <NumberInput
          id={`${labelPrefix}-min-val`}
          label={`Below ${suffix}`}
          value={minValue}
          onChange={(e, { value }) => onMinValue(value)}
          step={1}
        />
      )}
      <div className="settings-page__toggle-row">
        <span className="settings-page__toggle-label">{labelPrefix} — maximum</span>
        <Toggle id={`${labelPrefix}-max-en`} toggled={maxEnabled} onToggle={() => onMaxEnabled(!maxEnabled)} hideLabel />
      </div>
      {maxEnabled && (
        <NumberInput
          id={`${labelPrefix}-max-val`}
          label={`Above ${suffix}`}
          value={maxValue}
          onChange={(e, { value }) => onMaxValue(value)}
          step={1}
        />
      )}
    </>
  );
}
